import { DOCUMENT, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Inject,
  Renderer2,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, delay, filter, forkJoin, of, Subject } from 'rxjs';
import packageInfo from '../../package.json';
import { environment } from '../environments/environment';
import { DogBreedService } from './core/services/dog-breed-service';
import { PushService } from './core/services/push-service';
import { RestApiService } from './core/services/rest-api-service';
import { BaseComponent } from './shared/component-base';

import { AgeRange } from '@capgo/capacitor-age-range';
import { NativeAppService } from './core/services/native-app.service';
import { DialogService } from './core/services/dialog-service';
import { AgePickerDialogComponent } from './shared/dialogs/age-picker-dialog/age-picker-dialog.component';
import { MatDialog } from '@angular/material/dialog';

export const marketingCookiesUpdated: Subject<void> = new Subject<void>();

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends BaseComponent {
  title = 'GloboDog';
  public version: string = packageInfo.version;

  appLoaded = signal<boolean>(false);
  canShowBackButton = signal<boolean>(false);
  private backButtonBlackList = [
    '/register-for-provider',
    '/register-for-owner',
    '/provider-payment-data',
    // '/provider-payment-preview',
    '/forgot-password',
    '/provider/',
    '/payment-successful',
    '/account-activate',
    '/register-success',
  ];

  private backButtonQueryParamBlackList = ['saved=true'];

  // cookie key for visibility of marketing cookies
  private STORAGE_KEY = 'C_SETTINGS';
  private scriptAlreadyInserted = false;

  public insertMarketingScript() {
    // const script = document.createElement('script');
    // script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NSMJMV4Z');`;
    // const marketingScript = this.renderer.createElement('script');
    // if (marketingScript) {
    //   // insert marketing script to header
    //   marketingScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NSMJMV4Z');`;
    //   const head = document.getElementsByTagName('head')[0];
    //   head.insertBefore(marketingScript, head.firstChild);
    // }
    // // insert noscript script to body
    // const bodyScript = this.renderer.createElement('noscript');
    // if (bodyScript) {
    //   bodyScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NSMJMV4Z" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    //   this.renderer.appendChild(document.body, bodyScript);
    //   this.scriptAlreadyInserted = true;
    // }
  }

  private loadMarketingCookies() {
    const marketingCookies = localStorage.getItem(this.STORAGE_KEY);
    if (marketingCookies) {
      try {
        const parsedMC = JSON.parse(marketingCookies);
        if (parsedMC?.performanceAndMarketingCookies) {
          if (this.scriptAlreadyInserted) {
            return;
          }
          this.insertMarketingScript();
        }
      } catch (e) {
        console.error(
          'Error while parsing marketing cookies JSON',
          marketingCookies,
          e
        );
      }
    }
  }

  public isDevelopmentBuild = false;

  constructor(
    private translate: TranslateService,
    private router: Router,
    protected location: Location,
    private renderer: Renderer2,
    private dogBreedService: DogBreedService,
    private pushService: PushService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();

    this.isDevelopmentBuild = !environment.production;

    this.setAgeRange();

    marketingCookiesUpdated.subscribe(() => {
      this.loadMarketingCookies();
    });

    this.loadMarketingCookies();

    const possibleLanguages = ['en', 'hu', 'it', 'de'];
    this.translate.addLangs(possibleLanguages);
    this.translate.setDefaultLang('hu');

    const browserLang = translate.getBrowserLang() ?? 'en';
    const prefLang = localStorage.getItem('PREF_LANG');

    let firstUrlSet = false;
    this.translate.use(prefLang || browserLang || 'en');

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(ev => {
        if (!firstUrlSet) {
          const navigatedUrl = ev.url.split('/')?.[1];
          if (possibleLanguages.includes(navigatedUrl)) {
            this.translate.use(navigatedUrl);
            this.translate.setDefaultLang(navigatedUrl);
          } else if (prefLang) {
            this.translate.use(prefLang);
          } else if (possibleLanguages.includes(browserLang)) {
            this.translate.use(browserLang);
            this.translate.setDefaultLang(browserLang);
          } else {
            this.translate.use('en');
            this.translate.setDefaultLang('en');
          }
        }

        firstUrlSet = true;
      });

    const getMeObservable = this.restApiService.getMe().pipe(
      delay(500),
      catchError(() => of(null))
    );

    const getDogBreedsObservable = this.dogBreedService.getNewDogBreeds();

    forkJoin({
      getMeResult: getMeObservable,
      getDogBreedsResult: getDogBreedsObservable,
    }).subscribe({
      next: res => {
        this.appLoaded.set(true);

        // if there is a logged in user on first start and we are on mobile, then register push token
        if (this.nativeAppService.isNativePlatform() && res.getMeResult) {
          this.registerPush();
        }
      },
      error: _err => {
        console.error('err: ', _err);
        this.appLoaded.set(true);
      },
    });

    if (this.nativeAppService.isNativePlatform()) {
      // if on mobile, register push token after login
      this.restApiService.userLoggedIn$.subscribe(() => {
        this.registerPush();
      });

      // hijack back button so it doesn't exit the app
      CapacitorApp.addListener('backButton', event => {
        console.log('back pressed', event);

        if (event.canGoBack) {
          window.history.back();
        } else {
          CapacitorApp.exitApp().catch(e => console.error(e));
        }
      });
    }

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(ev => {
        const url = ev?.url?.split('?')?.[0];
        const queryParam = ev?.url?.split('?')?.[1];
        this.canShowBackButton.set(
          this.backButtonBlackList.every(bl => !url.includes(bl))
        );

        if (this.backButtonQueryParamBlackList.some(bl => queryParam === bl))
          this.canShowBackButton.set(false);
      });

    this.nativeAppService.clearBadge();
  }

  private registerPush(): void {
    this.pushService.registerNotifications().then(registered => {
      if (registered) {
        this.pushService.addListeners();
      }
    });
  }

  public ref = inject(ChangeDetectorRef);

  public ageOk = true;
  private getLocalStorageAgeOk() {
    return localStorage.getItem('ageOk') == '1';
  }

  private setLocalStorageAgeOk(ok: boolean) {
    localStorage.setItem('ageOk', ok ? '1' : '0');
  }

  private async setAgeRange() {
    if (!this.nativeAppService.isNativePlatform()) {
      this.ageOk = true;
      return;
    }
    if (this.getLocalStorageAgeOk()){
      return;
    }

    // Request age range with default gates (13, 16, 18)
    try {
      const result = await AgeRange.requestAgeRange({
        ageGates: [18],
      });

      //result.ageLower = 16; // test only, works on ios only, android requires install via google play
      switch (result.status) {
        case 'SHARING':
          if (result.ageLower && result.ageLower < 18) {
            this.ageOk = false;
          }
          else{
            this.setLocalStorageAgeOk(true);
          }
          console.log('Age range:', result.ageLower, '-', result.ageUpper);
          console.log('Source:', result.declarationSource);
          break;
        case 'DECLINED_SHARING':
          console.log('User declined to share age');
          this.showAgePicker();
          break;
        case 'NOT_AVAILABLE':
          console.log('Age range API not available');
          this.showAgePicker();
          break;
        case 'ERROR':
          console.log('Error occurred');
          this.showAgePicker();
          break;
      }
    } catch (e) {
      console.error('age range error detected: ', e);
    }

    // this.ageOk = false; // test only
    this.ref.detectChanges();
  }

  private readonly dialog = inject(MatDialog);
  public showAgePicker() {
    const dialogRef = this.dialog.open(AgePickerDialogComponent);

    dialogRef.afterClosed().subscribe({
      next: (res: any) => {
        console.log('age res', res);
        this.ageOk = res;
        this.setLocalStorageAgeOk(res);
        this.ref.detectChanges();
      },
      error: err => {
        console.error('Error while editing dog breed', err);
      },
    });
  }
}
